import { useState, useRef, useEffect, useCallback } from 'react';

import { NONE_SENTINEL } from './filterConstants';

/**
 * Multi-select filter dropdown.
 * Menu is position:fixed so it overlays the page and isn't clipped by
 * the sidebar's overflow:auto.
 *
 * Props:
 *   options  — [{ value: string, count: number }]
 *   value    — string[]  (selected values)
 *   onChange — (string[]) => void
 *   disabled — bool
 */
function FilterSelect({ options, value, onChange, disabled, formatLabel }) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const triggerRef = useRef(null);

  const toggle = v =>
    onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);

  const buttonLabel =
    value.length === 0 ? 'All'
      : value.length === 1 ? (value[0] === NONE_SENTINEL ? (formatLabel?.(NONE_SENTINEL) ?? 'None') : value[0])
        : `${value.length} selected`;

  const reposition = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const menuHeight = 260;
    const openUpward = spaceBelow < menuHeight + 8 && r.top > menuHeight;
    setMenuStyle(openUpward
      ? { bottom: window.innerHeight - r.top + 2, left: r.left, width: r.width }
      : { top: r.bottom + 2, left: r.left, width: r.width }
    );
  }, []);

  const handleOpen = () => {
    if (disabled) return;
    if (!open) reposition();
    setOpen(o => !o);
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (triggerRef.current && !triggerRef.current.closest('.filter-select').contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Reposition on scroll / resize while open
  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  return (
    <div className="filter-select">
      <button
        ref={triggerRef}
        className={`fs-trigger${value.length ? ' has-value' : ''}${open ? ' open' : ''}`}
        onClick={handleOpen}
        disabled={disabled}
        type="button"
      >
        <span className="fs-value">{buttonLabel}</span>
        {value.length > 0 && (
          <span
            className="fs-clear"
            role="button"
            onClick={e => { e.stopPropagation(); onChange([]); }}
          >×</span>
        )}
        <span className="fs-arrow">▾</span>
      </button>

      {open && (
        <div className="fs-menu" style={menuStyle}>
          {options.length === 0 && (
            <div className="fs-empty">No options</div>
          )}
          {options.map(({ value: v, count }) => (
            <label key={v} className={`fs-item${value.includes(v) ? ' checked' : ''}`}>
              <input
                type="checkbox"
                checked={value.includes(v)}
                onChange={() => toggle(v)}
              />
              <span className="fs-item-label">{formatLabel ? formatLabel(v) : v}</span>
              <span className="fs-item-count">{count}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default FilterSelect;
